import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { StateCreator } from 'zustand';

import { listViewColumnService } from '@/services';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { getWorkspaceResourceTypeLabel } from '@/shared/components/workspace-resources';
import {
    appendEntity,
    createSingleFlightKey,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
    replaceOptimisticEntityById,
    runSingleFlight,
    upsertEntityById,
} from '@/stores/helpers/collections';
import { reduceEntityDelta } from '@/stores/helpers/delta';
import { ListViewColumn } from '@/types';

import { ListViewColumnActions, ListViewColumnState } from '../types';

export const createListViewColumnActions: StateCreator<
    ListViewColumnState & ListViewColumnActions,
    [['zustand/immer', never]],
    [],
    ListViewColumnActions
> = (set, get, store) => ({
    // Core
    fetchListViewColumns: async (params) => {
        set({ loading: true });

        try {
            const listViewColumns = await listViewColumnService.list(params);

            set({ listViewColumns, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load listViewColumns' });
        }
    },
    fetchListViewColumnById: async (id) => {
        set((state) => {
            state.loadingListViewColumn = true;
            state.error = undefined;
        });

        try {
            const listViewColumn = await listViewColumnService.getById(id);

            set({ selectedListViewColumn: listViewColumn, loadingListViewColumn: false });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch listViewColumn detail';
                state.loadingListViewColumn = false;
            });
        }
    },
    clearSelectedListViewColumn: () => {
        set((state) => {
            state.selectedListViewColumn = null;
        });
    },
    createListViewColumn: async (input) =>
        runSingleFlight(createSingleFlightKey('listViewColumns:create', input), async () => {
            const tempId = `temp-${Date.now()}`;
            const optimistic = {
                id: tempId,
                isHidden: false,
                order: get().listViewColumns.length,
                ...input,
            } as ListViewColumn;

            set({ listViewColumns: appendEntity(get().listViewColumns, optimistic) });

            try {
                const listViewColumn = await listViewColumnService.create(input);

                set({ listViewColumns: replaceOptimisticEntityById(get().listViewColumns, tempId, listViewColumn) });

                return listViewColumn;
            } catch (error) {
                captureException(error);

                set({
                    listViewColumns: removeEntityById(get().listViewColumns, tempId),
                    error: 'Failed to create listViewColumn',
                });
            }
        }),
    updateListViewColumn: async (id, updates) => {
        const prev = get().listViewColumns.find((f) => f.id === id);
        if (!prev) {
            return;
        }

        set({
            listViewColumns: mergeEntityById(get().listViewColumns, id, updates),
            selectedListViewColumn:
                get().selectedListViewColumn?.id === id
                    ? mergeEntity(get().selectedListViewColumn!, updates)
                    : get().selectedListViewColumn,
        });

        try {
            const listViewColumn = await listViewColumnService.update(id, updates);

            set({
                listViewColumns: mergeEntityById(get().listViewColumns, id, listViewColumn),
                selectedListViewColumn:
                    get().selectedListViewColumn?.id === id
                        ? mergeEntity(get().selectedListViewColumn!, listViewColumn)
                        : get().selectedListViewColumn,
            });
        } catch (error) {
            captureException(error);

            set({
                listViewColumns: replaceEntityById(get().listViewColumns, id, prev),
                selectedListViewColumn: get().selectedListViewColumn?.id === id ? prev : get().selectedListViewColumn,
                error: 'Failed to update listViewColumn',
            });
        }
    },
    deleteListViewColumn: async (id) => {
        const prev = get().listViewColumns;

        set({
            listViewColumns: removeEntityById(get().listViewColumns, id),
            selectedListViewColumn: get().selectedListViewColumn?.id === id ? null : get().selectedListViewColumn,
        });

        try {
            await listViewColumnService.delete(id);
        } catch (error) {
            captureException(error);

            set({ listViewColumns: prev, error: 'Failed to delete listViewColumn' });
        }
    },
    applyListViewColumnDelta: (packet) => {
        if (packet.entity !== 'listViewColumn') {
            return;
        }

        const next = reduceEntityDelta<ListViewColumn>(get().listViewColumns, get().selectedListViewColumn, packet);

        if (!next) {
            return;
        }

        set({
            listViewColumns: next.items,
            selectedListViewColumn: next.selected,
        });
    },
    // - Aliases
    duplicateListViewColumn: async (id) => {
        const prev = get().listViewColumns;
        const resourceTypeLabel = getWorkspaceResourceTypeLabel('list-view-column');

        if (!prev) {
            return;
        }

        try {
            const duplicated = await listViewColumnService.duplicate(id);

            set({ listViewColumns: upsertEntityById(get().listViewColumns, duplicated, { prepend: true }) });

            toast(`${resourceTypeLabel} Duplicated`, {
                icon: createElement(BiDuplicate, { size: '1rem' }),
                description: duplicated.label,
            });

            return duplicated;
        } catch (error) {
            captureException(error);

            set({ listViewColumns: prev, error: 'Failed to duplicate listViewColumn' });

            toast.error(`Unable to Duplicate ${resourceTypeLabel}`);
        }
    },
    // Helper
    reorderListViewColumns: (ordered: ListViewColumn[]) =>
        set((state) => ({
            listViewColumns: ordered,
        })),
});
